<template>
  <form @submit.prevent="formSubmit">
    <h1 class="container-title" :class="[`text-${titleAlignment}`]">
      {{ title ? "" : $t(`global.${company.name}`) }} {{ title ? title : "Sign Up" }}
    </h1>
    <FormMessage :errors="errors" v-if="errors" />
    <FormInput
      v-if="signupFields.longform"
      name="firstName"
      v-model="signupFields.firstName"
      testId="first-name-input"
      :placeholder="$t('global.first-name')"
      :label="`${$t('global.first-name')} *`"
      :errors="validation.fields.firstName.errors"
      :dirty="validation.fields.firstName.dirty"
    />
    <FormInput
      v-if="signupFields.longform"
      name="lastName"
      v-model="signupFields.lastName"
      testId="last-name-input"
      :placeholder="$t('global.last-name')"
      :label="`${$t('global.last-name')} *`"
      :errors="validation.fields.lastName.errors"
      :dirty="validation.fields.lastName.dirty"
    />
    <FormInput
      name="email"
      v-model="signupFields.email"
      testId="email-input"
      :placeholder="$t('global.email')"
      :label="`${$t('global.email')} *`"
      :errors="validation.fields.email.errors"
      :dirty="validation.fields.email.dirty"
    />
    <FormInput
      name="password"
      v-model="signupFields.password"
      type="password"
      testId="password-input"
      :placeholder="$t('global.password')"
      :label="`${$t('global.password')} *`"
      :errors="validation.fields.password.errors"
      :dirty="validation.fields.password.dirty"
      :passwordMeter="true"
    />
    <FormCheckbox
      v-if="gdpr"
      name="marketingEmails"
      v-model="signupFields.marketingEmails"
      testId="marketing-emails-input"
      :label="$t('global.gdpr-notice')"
      :errors="validation.fields.marketingEmails.errors"
      :dirty="validation.fields.marketingEmails.dirty"
    />
    <FormCheckbox
      name="acceptTerms"
      v-model="signupFields.acceptTerms"
      testId="accept-terms-input"
      :label="`I have read and agree to the <a href='https://www.nexcess.net/policies/terms-of-service' target='_blank'>Terms of Service</a> and acknowledge receipt of the <a href='https://www.nexcess.net/policies/privacy-policy' target='_blank'>Privacy Policy</a>.`"
      :errors="validation.fields.acceptTerms.errors"
      :dirty="validation.fields.acceptTerms.dirty"
    />
    <div class="mt-3" v-if="gdpr">{{ $t("global.terms-of-service-gdpr") }}</div>
    <div class="form-actions">
      <template v-if="useRecaptcha">
        <template v-if="recaptchaType === 'invisible'">
          <Btn
            v-if="enableBack"
            variant="light"
            class="mr-2"
            type="button"
            :disabled="loading"
            @click="
              () => {
                backAction();
              }
            "
          >
            Back
          </Btn>
          <vue-recaptcha
            ref="recaptchaContainer"
            :sitekey="recaptchaSiteKey"
            :loadRecaptchaScript="true"
            @verify="formSubmit"
            @expired="onExpired"
            class="recaptcha-container"
          >
            <Btn
              variant="primary"
              type="submit"
              :disabled="loading"
              :loading="loading"
              data-test="signup-button"
            >
              Sign Up
            </Btn>
          </vue-recaptcha>
        </template>
        <template v-else>
          <vue-recaptcha
            ref="recaptchaContainer"
            :sitekey="recaptchaSiteKey"
            :loadRecaptchaScript="true"
            @verify="onVerify"
            @expired="onExpired"
            class="recaptcha-container"
          ></vue-recaptcha>
          <Btn
            v-if="enableBack"
            variant="light"
            class="mr-2"
            type="button"
            :disabled="loading"
            @click="
              () => {
                backAction();
              }
            "
          >
            Back
          </Btn>
          <Btn
            variant="primary"
            type="submit"
            :disabled="loading"
            :loading="loading"
            data-test="signup-button"
          >
            Sign Up
          </Btn>
        </template>
      </template>
      <template v-else>
        <Btn
          v-if="enableBack"
          variant="light"
          class="mr-2"
          type="button"
          :disabled="loading"
          @click="
            () => {
              backAction();
            }
          "
        >
          Back
        </Btn>
        <Btn
          variant="primary"
          type="submit"
          :disabled="loading"
          :loading="loading"
          data-test="signup-button"
        >
          Sign Up
        </Btn>
      </template>
      <div class="signup-links">
        <div class="sign-up my-2 mt-4">
          Already have an account?
          <a href="javascript:void(0)" @click="loginAction()">{{ $t("global.login") }}.</a>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import constants from "@/utils/constants";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import { fromServer } from "@/utils/translations";
import { passwordStrength as passwordStrengthRule } from "leatherman-js/utils/formatters/password";
import router from "@/router";
import { signup as signupValidationRules } from "leatherman-js/utils/validations/forms";
import store from "@/store";
import useCompany from "@/utils/useCompany";
import useValidator from "@/utils/useValidator";
import VueRecaptcha from "vue-recaptcha";

export default {
  name: "SignupForm",
  components: {
    Btn,
    FormInput,
    FormCheckbox,
    FormMessage,
    VueRecaptcha,
  },
  props: {
    loginAction: {
      type: Function,
      default: () => {
        router.push({ name: constants.LOGIN_ROUTE });
      },
    },
    longform: {
      type: Boolean,
      default: false,
    },
    title: String,
    titleAlignment: {
      type: String,
      default: "center",
    },
    backAction: Function,
    enableBack: Boolean,
  },
  setup(props, { emit, root }) {
    // display variables
    const loading = ref(false);
    const errors = ref(null);
    const gdpr = computed(() => store.state.auth.gdpr);

    // signup variables and validation
    const signupFields = reactive({
      email: null,
      password: null,
      lastName: null,
      firstName: null,
      recaptchaToken: null,
      acceptTerms: false,
      longform: props.longform,
      marketingEmails: false,
    });
    const { validator } = useValidator();
    const validation = validator(
      signupFields,
      signupValidationRules,
      {},
      {
        firstName: {
          requiredIf: root.$t("validation.first-name-required"),
        },
        lastName: {
          requiredIf: root.$t("validation.last-name-required"),
        },
        email: {
          required: root.$t("validation.email-required"),
          email: root.$t("validation.invalid-email"),
        },
        password: {
          required: root.$t("validation.password-required"),
        },
        acceptTerms: {
          equal: root.$t("validation.accept-terms-equal-true"),
        },
      }
    );
    const passwordStrength = computed(() => {
      return passwordStrengthRule(signupFields.password);
    });

    //recaptcha variables and methods
    const recaptchaType = ref(process.env.VUE_APP_RECAPTCHA_TYPE);
    const recaptchaSiteKey = ref(process.env.VUE_APP_RECAPTCHA_SITE_KEY);
    const recaptchaContainer = ref(null);
    const useRecaptcha = computed(() => {
      return store.state.settings.signUpRecaptchaEnabled;
    });

    const formSubmit = (token) => {
      if (recaptchaType.value === "invisible") {
        onVerify(token);
      }

      submit();
    };

    const onVerify = (token) => {
      signupFields.recaptchaToken = token;
    };

    const onExpired = () => {
      resetRecaptcha();
    };

    const resetRecaptcha = () => {
      signupFields.recaptchaToken = null;
      if (recaptchaContainer.value) {
        recaptchaContainer.value.reset();
      }
    };

    const { company } = useCompany();

    // submit the form
    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          loading.value = true;
          store
            .dispatch("auth/signup", {
              email: signupFields.email,
              firstName: signupFields.firstName,
              lastName: signupFields.lastName,
              password: signupFields.password,
              recaptchaToken: signupFields.recaptchaToken,
              acceptTerms: signupFields.acceptTerms,
              marketingEmails: signupFields.marketingEmails,
            })
            .then(() => {
              emit("success");
            })
            .catch((e) => {
              resetRecaptcha();
              // if two factor is required, show the two factor code field
              errors.value = fromServer(e.request.response);
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {
          resetRecaptcha();
        });
    };

    // component is mounted; reset recaptcha
    onMounted(() => {
      resetRecaptcha();
    });

    return {
      errors,
      signupFields,
      passwordStrength,
      loading,
      onExpired,
      onVerify,
      recaptchaSiteKey,
      recaptchaContainer,
      resetRecaptcha,
      submit,
      useRecaptcha,
      validation,
      formSubmit,
      company,
      recaptchaType,
      gdpr,
    };
  },
};
</script>

<style lang="scss" scoped>
.signup-links {
  font-size: 0.75rem;
  text-align: center;
  .forgot {
    margin-bottom: 1rem;
  }
}
</style>
